import React, { useState, useEffect } from 'react';
import { C1SignupData } from '../C1SignupFlow';
import { useSignature } from '../../../hooks/useSignature';
import { useAgreementPdf } from '../../../hooks/useAgreementPdf';
import { useDraftDocuments } from '../../../hooks/useDraftDocuments';
import { resolveEntityType } from '../../../hooks/useAbnLookup';
import {
  StepHeader,
  InfoGrid,
  SignaturePad,
  DocumentList,
  AgreementCheckboxes,
  StepNavButtons,
} from '../components';
import type { DocumentItem } from '../components';

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
}

const ReviewSign: React.FC<StepProps> = ({ data, onNext, onCancel }) => {
  const [reviewConfirmed, setReviewConfirmed] = useState(data.reviewConfirmed || false);
  const [agreedToTerms, setAgreedToTerms] = useState(data.agreedToTerms || false);
  const [printedName, setPrintedName] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  // ── Hooks ───────────────────────────────────────────────────────────────────
  const signature = useSignature({
    initialTyped: data.signatureData,
    initialDrawn: data.drawnSignature,
    initialMode: (data.signatureMode as 'typed' | 'drawn') || 'typed',
  });

  const { documents: draftDocs } = useDraftDocuments();

  const companyName = data.partyName || 'Your Company';
  const todayDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // When switching to drawn mode, carry over the typed name so the Name field isn't blank
  useEffect(() => {
    if (signature.signatureMode === 'drawn' && !printedName && signature.typedSignature.trim()) {
      setPrintedName(signature.typedSignature.trim());
    }
  }, [signature.signatureMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSignature = signature.getActiveSignatureData();
  const pdfParams = {
    partyName: companyName,
    partyAbn: data.partyAbn,
    partyAcn: data.partyAcn,
    partyAddress: data.partyAddress,
    partyEmail: data.partyEmail,
    agreementDate: todayDate,
    signatoryName: signature.signatureMode === 'typed'
      ? (activeSignature.typed || undefined)
      : (printedName.trim() || signature.typedSignature.trim() || undefined),
    drawnSignature: activeSignature.drawn || undefined,
    signatureMode: signature.signatureMode,
    ackTermsOfUse: agreedToTerms,
    ackPrivacyPolicy: agreedToTerms,
    ackServicesAgreement: reviewConfirmed,
    ackAuthorisedToSign: agreedToTerms,
  };

  const { filledPdfUrl, isPdfLoading, generateSignedPdfBytes, uploadSignedPdf } = useAgreementPdf(pdfParams);

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateForm = () => {
    setError('');
    if (!reviewConfirmed) { setError('Please confirm that you have reviewed all information'); return false; }
    if (!agreedToTerms) { setError('Please agree to the terms and conditions'); return false; }
    if (!signature.hasSignature) { setError('Please provide your electronic signature (typed or drawn)'); return false; }
    if (signature.signatureMode === 'drawn' && !printedName.trim()) { setError('Please enter your full name (printed) to accompany your drawn signature'); return false; }
    return true;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleNext = async () => {
    if (isProcessing) return;
    if (!validateForm()) return;
    setIsProcessing(true);

    try {
      let signedPdfBase64: string | undefined;
      let signedPdfS3Key: string | undefined;

      try {
        const pdfBytes = await generateSignedPdfBytes(pdfParams);

        const referenceDocs = draftDocs.filter(d => d.fileName !== 'agreement.pdf' && !d.key?.startsWith('drafts/austrac/'));
        const result = await uploadSignedPdf(pdfBytes, referenceDocs);
        signedPdfS3Key = result.s3Key;
        signedPdfBase64 = result.base64;
      } catch {
        // Continue without PDF
      }

      const sigData = signature.getActiveSignatureData();
      onNext({
        reviewConfirmed,
        signatureData: sigData.typed,
        drawnSignature: sigData.drawn,
        agreedToTerms,
        signatureAccepted: true,
        signatureMode: signature.signatureMode,
        signedAgreementPdfBase64: signedPdfBase64,
        signedAgreementPdfS3Key: signedPdfS3Key,
      });
    } catch {
      setIsProcessing(false);
    }
  };

  // ── Build document list ─────────────────────────────────────────────────────

  const documents: DocumentItem[] = [
    {
      id: 'agreement',
      icon: '📋',
      title: 'ARCaml Customer Services Agreement',
      description: 'AML/CTF services engagement agreement with iDeed Pty Ltd',
      type: 'pdf-filled',
    },
    ...draftDocs
      .filter(d => d.fileName !== 'agreement.pdf' && !d.key.startsWith('drafts/austrac/'))
      .map(d => ({
        id: d.fileName,
        icon: '📄',
        title: d.fileName.replace(/[-_]/g, ' ').replace(/\.pdf$/i, ''),
        description: '',
        type: 'pdf-static' as const,
        url: d.downloadUrl,
      })),
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: '100%' }}>
      <StepHeader
        icon="✍️"
        title="Review &amp; Sign"
        description="Review your information, read the agreement documents, and provide your electronic signature."
      />

      {/* Your Information */}
      <InfoGrid
        title="Your Information"
        items={[
          { label: 'Party Name', value: data.partyName || '—', icon: '🏢' },
          { label: 'ABN / ACN', value: data.partyAbn || data.partyAcn || '—', icon: '🔢' },
          { label: 'Entity Type', value: resolveEntityType(data.partyEntityType) || resolveEntityType(data.partyType) || data.partyEntityType || '—', icon: '🏛️' },
        ]}
      />

      {/* Agreement Documents */}
      <DocumentList
        documents={documents}
        renderFilledDetails={() => (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
            padding: '12px 16px',
            background: '#F0F7FF',
            borderBottom: '1px solid #BFDBFE',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#64748B', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Company:</span>
              <span style={{ color: '#0F2647', fontSize: 13, fontWeight: 600 }}>{companyName}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#64748B', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Date:</span>
              <span style={{ color: '#0F2647', fontSize: 13, fontWeight: 600 }}>{todayDate}</span>
            </div>
          </div>
        )}
        renderFilledViewer={() => (
          <div style={{ position: 'relative' }}>
            {isPdfLoading && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: '#F8FAFC', zIndex: 2,
              }}>
                <div style={{
                  width: 36, height: 36,
                  border: '3px solid #E2E8F0', borderTop: '3px solid #0F2647',
                  borderRadius: '50%', animation: 'spin 1s linear infinite',
                }} />
                <p style={{ color: '#64748B', fontSize: 12, marginTop: 10 }}>Loading agreement…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {filledPdfUrl ? (
              <iframe
                src={filledPdfUrl}
                title="ARCaml Customer Services Agreement"
                onLoad={() => {}}
                style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
              />
            ) : !isPdfLoading && (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: 200, color: '#64748B', fontSize: 13, gap: 8,
              }}>
                <span style={{ fontSize: 28 }}>📄</span>
                Agreement preview unavailable — the document will be generated on submission.
              </div>
            )}
          </div>
        )}
      />

      {/* Electronic Signature */}
      <SignaturePad
        mode={signature.signatureMode}
        onModeChange={signature.setSignatureMode}
        typedValue={signature.typedSignature}
        onTypedChange={signature.setTypedSignature}
        onDrawnChange={signature.setDrawnSignatureUrl}
        drawnDataUrl={signature.drawnSignatureUrl}
        printedName={printedName}
        onPrintedNameChange={setPrintedName}
      />

      {/* Agreement Checkboxes */}
      <AgreementCheckboxes
        checkboxes={[
          {
            id: 'review-confirmed',
            checked: reviewConfirmed,
            onChange: setReviewConfirmed,
            label: 'I confirm that I have carefully reviewed all information provided and it is accurate and complete',
          },
          {
            id: 'agreed-to-terms',
            checked: agreedToTerms,
            onChange: setAgreedToTerms,
            label: (
              <>
                I have read and agree to the <strong>ARCaml Customer Services Agreement</strong>,{' '}
                <strong>Terms of Use</strong>, <strong>Privacy Policy</strong>, and <strong>Cookie Policy</strong>
              </>
            ),
          },
          {
            id: 'signature-accepted',
            checked: signature.hasSignature,
            onChange: () => {},
            readOnly: true,
            label: 'By providing my signature above, I electronically sign this agreement and accept legal responsibility',
          },
        ]}
      />

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 10,
          color: '#DC2626',
          fontSize: 13,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span>⚠️</span> {error}
        </div>
      )}

      <StepNavButtons
        onCancel={onCancel}
        onContinue={handleNext}
        continueLabel="Continue to Payment →"
        disabled={!reviewConfirmed || !agreedToTerms || !signature.hasSignature}
        loading={isProcessing}
      />
    </div>
  );
};

export default ReviewSign;
